import Component from '../../js/_Component';

const CNAME = 'c-base-stat';

export default class BaseStat extends Component {
	/**
	 * Animation duration, in seconds.
	 */
	private readonly ANIMATION_DURATION = 1;

	/**
	 * As number animates, it follows a curve mapped to X^f.
	 * f = 1 is linear, 0 < f < 1 starts fast and slows down, f > 1 starts slow
	 * and ends faster.
	 */
	private readonly INTERPOLATION_FACTOR = 1 / 4;

	/**
	 * Prefix string before the animated content.
	 */
	private prefix = '';

	/**
	 * Suffix string after the animated content.
	 */
	private suffix = '';

	/**
	 * The value to animate to.
	 */
	private value = 0;

	/**
	 * How many digits should be shown after the decimal, if any.
	 */
	private decimals = 0;

	/**
	 * The container holding the animated value.
	 */
	private contentContainer: HTMLElement;

	/**
	 * The time that the animation was started, or 0 if not started yet.
	 */
	private animationStarted = 0;

	constructor(element: HTMLElement) {
		super(element);

		this.contentContainer = element.querySelector(`.${CNAME}--content`);

		if (this.element.dataset.static === undefined && this.parseContent()) {
			this.finalWidthCalc(this.value);
			this.renderValue(0);
			const observer = new IntersectionObserver((e) => this.startAnimation(e), {
				threshold: 0.25,
			});
			observer.observe(this.element);
		}
	}

	private startAnimation(entries: IntersectionObserverEntry[]) {
		if (entries[0].isIntersecting) {
			if (!this.animationStarted) {
				requestAnimationFrame((t) => {
					this.animationStarted = t;
					this.animate(t);
				});
			}
		}
	}

	private animate(time: number) {
		// Get time passed since animation start.
		let delta = (time - this.animationStarted) / 1000;

		// Determine how far along in the animation the time passed is.
		delta = Math.min(1.0, delta / this.ANIMATION_DURATION);

		// Apply the animation factor.
		delta = delta ** this.INTERPOLATION_FACTOR;

		// Show the value based on the final animation factor.
		this.renderValue(delta * this.value);

		// Not finished animating if the delta hasn't reached the max.
		if (delta < 1.0) {
			requestAnimationFrame((t) => this.animate(t));
		}
	}

	private renderValue(value: number) {
		// Start with the integer part of the number.
		let outValue = Math.floor(value).toString();

		// Add commas to the number.
		if (this.element.dataset.format !== undefined) {
			outValue = outValue.replace(/\d(?=(?:\d\d\d)+$)/g, '$&,');
		}

		// Strip digits beyond the requested decimals.
		if (this.decimals) {
			outValue += value.toString().substr(Math.floor(value).toString().length, this.decimals + 1);
		}

		// Display the value with the prefix and suffix.
		let finalOutput = this.prefix + outValue + this.suffix as string;
		this.contentContainer.textContent = finalOutput;

		// Update the length for non-jitter-purposes
		//this.contentContainer.dataset.length = `${finalOutput.length}`;

		/*
		// Force a min-width?
		console.log("parse",this.contentContainer.offsetWidth,this.contentContainer);
		this.contentContainer.style.minWidth = `${this.contentContainer.offsetWidth}px`;
		*/
	}

	/**
	 * Attempts to parse a numeric value, prefix, and suffix from the
	 * content.
	 * @returns True if content had numeric data that was parsed, false if it
	 * is assumed it was textual.
	 */
	private parseContent() {
		const content = this.contentContainer.textContent;
		const matchData = content.match(/^\s*(\D*)(\d+|\d[\d,]*(?:\.(\d+))?)(\D*)\s*$/);

		if (matchData) {
			this.prefix = matchData[1] || '';
			this.suffix = matchData[4] || '';
			this.value = parseFloat(matchData[2].replace(',', ''));
			this.decimals = (matchData[3] || '').length;
		}
		return !!matchData;
	}

	private finalWidthCalc(value: number) {
		// Start with the integer part of the number.
		let outValue = Math.floor(value).toString();

		// Add commas to the number.
		if (this.element.dataset.format !== undefined) {
			outValue = outValue.replace(/\d(?=(?:\d\d\d)+$)/g, '$&,');
		}

		// Strip digits beyond the requested decimals.
		if (this.decimals) {
			outValue += value.toString().substr(Math.floor(value).toString().length, this.decimals + 1);
		}

		// Update the length for non-jitter-purposes
		let finalOutput = this.prefix + outValue + this.suffix as string;
		this.contentContainer.dataset.length = `${finalOutput.length}`;
	}

}

Component.register(BaseStat, 'BaseStat');
